import { useEffect, useState } from 'react';

import { useQueryAssets } from '@/hooks/useAssets';
import { useGetRisks } from '@/hooks/useRisks';
import { AssetStatus, RiskStatus } from '@/types';
import { getRiskStatusLabel } from '@/utils/riskStatus.util';

/**
 * Reusable risk-selection step shared by the Generate Report and Generate LOA
 * wizards. Lets the user choose which risks an export covers and resolves that
 * choice to a `risk_keys` array.
 *
 * Resolution semantics (`resolvedRiskKeys`):
 *  - `'all'`       → `undefined` (export covers ALL open risks; no keys sent).
 *  - `'specific'`  → precedence order: (1) hand-picked keys if any (they win —
 *                    conservative); else (2) if tags are selected, the keys of
 *                    every open risk the server matched to the tag filter
 *                    (from the exhausted `specificQuery`); else (3) `undefined`
 *                    = all open risks.
 *  - `'dateRange'` → the keys of open risks whose chosen date field falls inside
 *                    `[startDate, endDate]`, resolved live via `useGetRisks`.
 *  - `'byAsset'`   → the keys of open risks linked to any of the selected assets
 *                    (via `HAS_VULNERABILITY|HAS_PORT|HAS_WEBPAGE`), resolved
 *                    live via `useGetRisks({ assetKeys })`; `undefined` until at
 *                    least one asset is picked.
 *
 * The hook instance is owned by the parent wizard (not the step component) so the
 * resolved keys are available at submit time.
 *
 * Pagination — IMPORTANT: `useGetRisks` is backed by `useInfiniteQuery` with a
 * 100-rows-per-page cap. Reading `query.data` alone would yield only the FIRST
 * page, silently capping the resolved scope (and the "specific" picker) at 100
 * risks — so a date-range/LOA export would omit every matching finding beyond
 * the first 100, and the picker could never reach the rest. To avoid that, this
 * hook auto-fetches every page to exhaustion (`fetchNextPage` while
 * `hasNextPage`) before exposing keys/options, and reports `isResolving` /
 * `isLoadingOptions` while pages are still loading so the wizards disable
 * Next/Generate until the COMPLETE set is loaded.
 */

export type RiskSelectionMode = 'all' | 'dateRange' | 'specific' | 'byAsset';

/** Which Risk date field the date-range filter compares against. */
export type RiskDateField = 'created' | 'visited';

/** A pickable open risk for the "specific" multi-select. */
export interface RiskOption {
  key: string;
  label: string;
  /** Full status code (e.g. `OC`), for StatusChip variant + label lookup. */
  status: string;
}

/** A pickable asset for the "byAsset" multi-select. */
export interface AssetOption {
  key: string;
  /** Human-readable display name (falls back to the identifier/DNS). */
  label: string;
  /** Secondary line — the asset identifier/DNS, shown muted under the label. */
  detail: string;
}

export interface RiskSelectionState {
  mode: RiskSelectionMode;
  setMode: (mode: RiskSelectionMode) => void;

  dateField: RiskDateField;
  setDateField: (field: RiskDateField) => void;
  startDate: string;
  setStartDate: (date: string) => void;
  endDate: string;
  setEndDate: (date: string) => void;

  selectedKeys: string[];
  setSelectedKeys: (keys: string[]) => void;

  /**
   * Tag values used to server-side filter the `'specific'` picker's open-risk
   * list via `useGetRisks`'s `advancedFilters.tags` (an `IN` filter). Empty
   * means "no tag constraint" — the picker returns every open risk. Non-empty
   * narrows both the picker options AND (when no keys are hand-picked) the
   * resolved `risk_keys` payload.
   */
  selectedSpecificTags: string[];
  setSelectedSpecificTags: (tags: string[]) => void;

  /** Asset keys picked in `'byAsset'` mode. */
  selectedAssetKeys: string[];
  setSelectedAssetKeys: (keys: string[]) => void;

  /**
   * Server-side search term for the `'byAsset'` asset picker. Drives
   * `useQueryAssets`'s fulltext search (key/name/dns/identifier) so the picker
   * never has to download the whole inventory — the user narrows by typing.
   */
  assetSearch: string;
  setAssetSearch: (search: string) => void;

  /**
   * The resolved `risk_keys` payload. `undefined` means "all open risks"
   * (mode `'all'`, an empty `'specific'` selection, a `'dateRange'` whose
   * bounds are not yet both set, or a `'byAsset'` with no asset picked yet).
   */
  resolvedRiskKeys: string[] | undefined;
  /**
   * True while the date-range OR by-asset query is fetching its matched keys
   * (including paging the full result set to exhaustion). The wizards gate
   * Next/Generate on this so the export never submits a capped/partial key set.
   */
  isResolving: boolean;
  /**
   * True when the `'specific'` mode's scope is well-defined and safe to
   * submit. Ready iff at least one of:
   *   - Hand-picked keys exist (`selectedKeys.length > 0`); OR
   *   - No tag filter is active (empty selection resolves to `undefined` =
   *     all open risks — an explicit, safe choice); OR
   *   - A tag filter IS active AND resolved to at least one key.
   * The false case that this flag guards is: tags selected but zero matches —
   * `resolvedRiskKeys` would be `[]`, which the backend
   * (report/service.go FetchRisks) treats as "all open risks", silently
   * exporting everything.
   */
  isSpecificScopeReady: boolean;
  /** Open risks available to pick in `'specific'` mode. */
  riskOptions: RiskOption[];
  /**
   * True while the `'specific'` picker is still loading its open-risk options
   * (including paging to exhaustion). Lets the picker distinguish "still
   * loading" from a genuinely empty result, so the empty state never lies.
   */
  isLoadingOptions: boolean;
  /** Pickable assets matching the current `assetSearch` (first page only). */
  assetOptions: AssetOption[];
  /**
   * True only while the asset picker's first page is loading. The picker does
   * NOT page to exhaustion (large inventories would stall it) — search narrows
   * the set server-side instead — so this never blocks on a full download.
   */
  isLoadingAssetOptions: boolean;
}

// Open risks only — the export wizards cover what's currently open. Mirrors the
// 'O' status code (RiskStatus.Opened) the risks table uses for "Open".
const OPEN_STATUS_FILTER = [RiskStatus.Opened];

// Assets worth scoping a report to — everything that's live (active passive,
// active low, active, frozen). Excludes Deleted/Pending/Unavailable so the
// picker only offers assets a finding could realistically be attributed to.
const PICKABLE_ASSET_STATUSES = [
  AssetStatus.ActivePassive,
  AssetStatus.ActiveLow,
  AssetStatus.Active,
  AssetStatus.Frozen,
];

export function useRiskSelection(): RiskSelectionState {
  const [mode, setMode] = useState<RiskSelectionMode>('all');
  const [dateField, setDateField] = useState<RiskDateField>('created');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [selectedSpecificTags, setSelectedSpecificTags] = useState<string[]>(
    []
  );
  const [selectedAssetKeys, setSelectedAssetKeys] = useState<string[]>([]);
  const [assetSearch, setAssetSearch] = useState('');

  const isDateRangeMode = mode === 'dateRange';
  const isSpecificMode = mode === 'specific';
  const isByAssetMode = mode === 'byAsset';
  const hasDateRange = !!startDate && !!endDate;
  const hasSelectedAssets = selectedAssetKeys.length > 0;

  // Date-range resolution. `useGetRisks` only honors `advancedFilters` (including
  // `dateRange`) when it runs in "drawer context" — i.e. `parentFilters` is set —
  // so the filter is carried via `parentFilters`, not `initialFilters` (which is
  // only the seed for the inert ephemeral-filter state). See useRisks.ts.
  const dateRangeQuery = useGetRisks({
    enabled: isDateRangeMode && hasDateRange,
    parentFilters: {
      advancedFilters: {
        status: OPEN_STATUS_FILTER,
        dateRange: { field: dateField, startDate, endDate },
      },
    },
  });

  // Open-risk list for the "specific" multi-select picker. When the user picks
  // tags, the filter is pushed to the server via `advancedFilters.tags` (a
  // single `tags IN [...]` filter — see useRisks.ts) so the picker only
  // materializes matching risks and the exhausted result set is a valid
  // `risk_keys` payload on its own (see `resolvedRiskKeys` below).
  const specificQuery = useGetRisks({
    enabled: isSpecificMode,
    parentFilters: {
      advancedFilters: {
        status: OPEN_STATUS_FILTER,
        ...(selectedSpecificTags.length > 0
          ? { tags: selectedSpecificTags }
          : {}),
      },
    },
  });

  // Asset list for the "byAsset" multi-select picker. Search is carried via
  // `parentFilters.search` — that survives useQueryAssets's `{ ...parentFilters,
  // ...localFilters }` merge (localFilters has no `search` key), whereas a
  // `parentFilters.advancedFilters` would be clobbered by localFilters' empty
  // `advancedFilters: {}`. So the status restriction is applied client-side on
  // `assetOptions` below, and search is pushed to the server here.
  const assetQuery = useQueryAssets({
    enabled: isByAssetMode,
    parentFilters: { search: assetSearch },
  });

  // By-asset resolution: open risks linked to any of the selected assets. Scopes
  // via `assetKeys` (a single `key IN [...]` Asset source — see useRisks.ts), so
  // one query covers every selected asset rather than one query per asset.
  const byAssetQuery = useGetRisks({
    enabled: isByAssetMode && hasSelectedAssets,
    parentFilters: {
      assetKeys: selectedAssetKeys,
      advancedFilters: {
        status: OPEN_STATUS_FILTER,
      },
    },
  });

  // Drive both infinite queries to exhaustion. `useGetRisks` pages 100 rows at a
  // time; without this, `.data` would only ever hold the first page. We fetch
  // the next page whenever one exists and we aren't already fetching — the
  // effect re-runs as each page lands (hasNextPage / isFetching change) until
  // `hasNextPage` is false, at which point `.data` holds the COMPLETE set.
  // Guarded on `hasNextPage` so there is no infinite loop.
  useEffect(() => {
    if (
      isDateRangeMode &&
      hasDateRange &&
      dateRangeQuery.hasNextPage &&
      !dateRangeQuery.isFetching
    ) {
      dateRangeQuery.fetchNextPage();
    }
  }, [
    isDateRangeMode,
    hasDateRange,
    dateRangeQuery.hasNextPage,
    dateRangeQuery.isFetching,
    dateRangeQuery.fetchNextPage,
  ]);

  useEffect(() => {
    if (
      isSpecificMode &&
      specificQuery.hasNextPage &&
      !specificQuery.isFetching
    ) {
      specificQuery.fetchNextPage();
    }
  }, [
    isSpecificMode,
    specificQuery.hasNextPage,
    specificQuery.isFetching,
    specificQuery.fetchNextPage,
  ]);

  // NOTE: the asset picker (assetQuery) is intentionally NOT paged to
  // exhaustion — on large tenants that would fire dozens of sequential
  // requests and stall the flow. Search narrows the set server-side instead.
  // The by-asset RISK resolution below IS exhausted, because `risk_keys` must
  // be the COMPLETE set or the export would silently drop findings.
  useEffect(() => {
    if (
      isByAssetMode &&
      hasSelectedAssets &&
      byAssetQuery.hasNextPage &&
      !byAssetQuery.isFetching
    ) {
      byAssetQuery.fetchNextPage();
    }
  }, [
    isByAssetMode,
    hasSelectedAssets,
    byAssetQuery.hasNextPage,
    byAssetQuery.isFetching,
    byAssetQuery.fetchNextPage,
  ]);

  const riskOptions: RiskOption[] = specificQuery.data.map(risk => ({
    key: risk.key,
    label: risk.title || risk.name,
    status: risk.status,
  }));

  // Restrict to pickable (live) statuses client-side — see the assetQuery note
  // above for why this can't go through parentFilters.advancedFilters.
  const assetOptions: AssetOption[] = assetQuery.data
    .filter(asset =>
      PICKABLE_ASSET_STATUSES.includes(asset.status as AssetStatus)
    )
    .map(asset => ({
      key: asset.key,
      label: asset.name || asset.identifier || asset.dns,
      detail: asset.identifier || asset.dns || '',
    }));

  const dateRangeKeys = dateRangeQuery.data.map(risk => risk.key);
  const byAssetKeys = byAssetQuery.data.map(risk => risk.key);

  // Keys of every risk the server matched the current tag filter to, exhausted.
  // Only used when tags are selected AND the user did not hand-pick anything —
  // in that case the tag filter alone defines the export scope.
  const specificTagMatchedKeys = specificQuery.data.map(risk => risk.key);

  let resolvedRiskKeys: string[] | undefined;
  if (mode === 'all') {
    resolvedRiskKeys = undefined;
  } else if (mode === 'specific') {
    // Precedence in 'specific' mode:
    //   1. Hand-picked keys always win (conservative — user explicitly chose).
    //   2. Else, if tag filter is active, the tag-matched keys become the scope
    //      (from the exhausted `specificQuery`).
    //   3. Otherwise, undefined = all open risks.
    if (selectedKeys.length > 0) {
      resolvedRiskKeys = selectedKeys;
    } else if (selectedSpecificTags.length > 0) {
      resolvedRiskKeys = specificTagMatchedKeys;
    } else {
      resolvedRiskKeys = undefined;
    }
  } else if (mode === 'byAsset') {
    // 'byAsset': resolved keys only once at least one asset is picked; otherwise
    // treat as unconstrained (undefined = all open risks). `byAssetKeys`
    // reflects the COMPLETE paged result (see the exhaustion effect above).
    resolvedRiskKeys = hasSelectedAssets ? byAssetKeys : undefined;
  } else {
    // 'dateRange': resolved keys only once both bounds are set; otherwise treat
    // as unconstrained (undefined = all open risks). `dateRangeKeys` reflects
    // the COMPLETE paged result (see the exhaustion effect above).
    resolvedRiskKeys = hasDateRange ? dateRangeKeys : undefined;
  }

  // Resolving while the initial page is pending, while more pages remain to be
  // fetched (`hasNextPage`), OR while the query is actively fetching
  // (`isFetching`). The last of these is critical for the `specific` tag-change
  // path: `useGetRisks` uses `keepPreviousData` (see useRisks.ts), so when the
  // user switches tags the query briefly reports `status === 'success'` +
  // `hasNextPage === false` while `isFetching === true` and `.data` still
  // holds the PREVIOUS tag's rows. Without gating on `isFetching`, Next /
  // Generate / Select-all would act on that stale key set.
  const isResolving =
    (isDateRangeMode &&
      hasDateRange &&
      (dateRangeQuery.status === 'pending' ||
        !!dateRangeQuery.hasNextPage ||
        dateRangeQuery.isFetching)) ||
    (isByAssetMode &&
      hasSelectedAssets &&
      (byAssetQuery.status === 'pending' ||
        !!byAssetQuery.hasNextPage ||
        byAssetQuery.isFetching)) ||
    // Specific mode, tags-only branch: the tag-scoped picker is the source of
    // truth for `risk_keys`, so the wizard must wait for it to page to
    // exhaustion. Skipped when the user hand-picks anything (selectedKeys wins
    // and is instantly resolved).
    (isSpecificMode &&
      selectedSpecificTags.length > 0 &&
      selectedKeys.length === 0 &&
      (specificQuery.status === 'pending' ||
        !!specificQuery.hasNextPage ||
        specificQuery.isFetching));

  // Same exhaustion gate for the "specific" picker so its empty state never
  // shows "no risks" while pages are still loading. Includes `isFetching` for
  // the tag-change case above — otherwise the picker would flash the previous
  // tag's rows in the moment between "old query success" and "new query
  // completes".
  const isLoadingOptions =
    isSpecificMode &&
    (specificQuery.status === 'pending' ||
      !!specificQuery.hasNextPage ||
      specificQuery.isFetching);

  // Whether the 'specific' scope is safe to advance/submit. Guards the
  // zero-match tag branch: with tags selected but no hand-picks, the resolved
  // key set is the exhausted `specificQuery.data`. If that resolves to []
  // (tag matched zero open risks), the backend
  // (report/service.go FetchRisks) treats `risk_keys: []` as "all open
  // risks" — so the export would silently cover everything. Callers
  // (GenerateReportWizard / GenerateLOAWizard) extend their `scopeConfigReady`
  // gate with this flag when mode === 'specific'.
  //
  // The `!isLoadingOptions` guard also disables progression while the
  // tag-scoped query is still resolving (initial or tag-change refetch), so
  // the wizard never advances on a stale/pending resolution.
  const isSpecificScopeReady = isSpecificMode
    ? selectedKeys.length > 0 ||
      selectedSpecificTags.length === 0 ||
      (!isLoadingOptions && specificTagMatchedKeys.length > 0)
    : true;

  // Only the first page matters for the picker (no exhaustion), so this reflects
  // just the initial/search-change load — never a full-inventory download.
  const isLoadingAssetOptions =
    isByAssetMode && assetQuery.status === 'pending';

  return {
    mode,
    setMode,
    dateField,
    setDateField,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    selectedKeys,
    setSelectedKeys,
    selectedSpecificTags,
    setSelectedSpecificTags,
    selectedAssetKeys,
    setSelectedAssetKeys,
    assetSearch,
    setAssetSearch,
    resolvedRiskKeys,
    isResolving,
    isSpecificScopeReady,
    riskOptions,
    isLoadingOptions,
    assetOptions,
    isLoadingAssetOptions,
  };
}

// Re-exported for callers that want the label for a risk status code.
export { getRiskStatusLabel };
